import { FC, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { createSelector } from "reselect";
import { DEFAULT_BOT_MESSAGE_DELAY_SECONDS } from "../../constants";
import { useBotTimeout } from "../../hooks/useBotTimeout";
import { useCanHandleBotStep } from "../../hooks/useCanHandleBotSteps";
import { sessionDataHandler } from "../../sessionDataHandler";
import {
    changeEngagementPhase,
    sendConversationMessage,
    setActiveBotStep,
    setActiveBotStepStatus,
    setIsEscalated,
    setShowMessageInput
} from "../../store/actions/genericActions";
import { ChatReducer } from "../../store/chat.reducer";
import { AppState, BotStepStatus, EngagementPhase } from "../../store/definitions";
import { SessionReducer } from "../../store/session.reducer";
import { BotStepType, CreateTaskBotStep } from "../../types/bots";

interface CreateTaskStepProps {
    botStep: CreateTaskBotStep;
}

const createTaskStepSelector = createSelector(
    (state: AppState) => state,
    (state) => ({
        conversation: state.chat.conversation,
        stateTaskAttributes: state.bot.taskAttributes,
        preEngagementData: state.session.preEngagementData,
        storeResponse: state.bot.storeResponse ?? false,
        storeResponseName: state.bot.storeResponseName,
        storeResponseAttributeIndex: state.bot.storeResponseAttributeIndex,
        conversationAttributes: state.bot.taskAttributes?.conversations as Record<string, unknown> | undefined
    })
);

export const CreateTaskStep: FC<CreateTaskStepProps> = ({ botStep }) => {
    const dispatch = useDispatch<ThunkDispatch<ChatReducer | SessionReducer, unknown, AnyAction>>();
    const { canHandleBotStep } = useCanHandleBotStep(botStep, {
        requiredStepType: BotStepType.CreateTask
    });
    const {
        conversation,
        stateTaskAttributes,
        preEngagementData,
        storeResponse,
        storeResponseName,
        storeResponseAttributeIndex,
        conversationAttributes
    } = useSelector(createTaskStepSelector);

    const handleTimeout = useBotTimeout({
        waitInSeconds: botStep.waitInSeconds ?? DEFAULT_BOT_MESSAGE_DELAY_SECONDS
    });

    const sendBotMessage = useCallback(
        async (message: string) => {
            dispatch(
                sendConversationMessage({
                    sentByBot: true,
                    conversation,
                    body: message,
                    storeResponse,
                    storeResponseAttributeIndex,
                    storeResponseName,
                    conversationAttributes
                })
            );
        },
        [
            conversation,
            sendConversationMessage,
            storeResponse,
            storeResponseAttributeIndex,
            storeResponseName,
            conversationAttributes
        ]
    );

    useEffect(() => {
        const handleBotStep = async () => {
            if (!canHandleBotStep) {
                return;
            }

            // Mark step as being processed, and hide message input
            dispatch(setActiveBotStepStatus(BotStepStatus.Processing));

            dispatch(setShowMessageInput(false));

            // adds some delay to the bot step
            await handleTimeout.start();

            // Display message with the twilio queue name and priority attached
            if (botStep.message) {
                sendBotMessage(botStep.message);
            }

            await sessionDataHandler.createTask({
                preEngagementData,
                conversationSid: conversation?.sid,
                twilioQueueName: botStep.queueId,
                taskAttributes: { ...stateTaskAttributes, ...botStep.taskAttributes },
                channel: botStep.taskChannel
            });

            dispatch(setIsEscalated(true));

            // Now we're being routed to a queue, allow use to input free text / upload media
            dispatch(setShowMessageInput(true));

            if (botStep.nextStepId) {
                dispatch(setActiveBotStepStatus(BotStepStatus.Unhandled));
                dispatch(setActiveBotStep(botStep.nextStepId));
            } else {
                dispatch(changeEngagementPhase({ phase: EngagementPhase.MessagingCanvas }));
            }
        };
        handleBotStep();
    }, [dispatch, sendBotMessage, botStep, canHandleBotStep]);

    return <></>;
};
